import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { UserAndContact } from 'types/UsersAndContact.types';
import ContactReasonField from './ContactReasonField';
import FunctionSelect from './FunctionSelect';
import { CONTACT_REASONS_TYPE } from './reasonsColorIcon';
import useSubmitContactForm from './useSubmitContactForm';
import TextFieldMask from 'components/TextFieldMask';

function ContactFormModal({
  open,
  setOpen,
  refetchContacts,
  initialItem,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<UserAndContact | boolean>>;
  refetchContacts: () => void;
  initialItem: UserAndContact | null;
}) {
  const { t } = useTranslation(['contacts', '_common', 'forms']);

  const initialValues: UserAndContact = initialItem || {
    name: '',
    phone: '',
    additionalPhone: '',
    function: null,
    contactsReason: CONTACT_REASONS_TYPE,
    email: '',
    questionPassword: '',
    answerPassword: '',
    answerCoercion: '',
    taxpayerIdentification: '',
    identificationNumber: '',
  };

  const { loading, onSubmit, validationSchema } = useSubmitContactForm({
    t,
    setOpen,
    refetchContacts,
  });

  const formik = useFormik({
    validationSchema,
    initialValues,
    enableReinitialize: true,
    onSubmit,
  });

  useEffect(() => {
    if (!open) {
      formik.resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      PaperProps={{
        sx: { width: 750 },
      }}
    >
      <DialogTitle>{t('contacts:new_contact')}</DialogTitle>
      <Box component="form" noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
        <DialogContent sx={{ pt: 0.8 }}>
          <TextField
            label={t('_common:name')}
            name="name"
            id="name"
            autoFocus={true}
            fullWidth
            helperText={formik.touched.name && formik.errors.name}
            value={formik.values.name}
            onChange={formik.handleChange}
            error={formik.touched.name && Boolean(formik.errors.name)}
            autoComplete="off"
          />
          <Box sx={{ display: 'flex', gap: 3 }}>
            <TextField
              type="tel"
              label={t('_common:phone')}
              name="phone"
              id="phone"
              fullWidth
              helperText={formik.touched.phone && formik.errors.phone}
              value={formik.values.phone}
              onChange={formik.handleChange}
              error={formik.touched.phone && Boolean(formik.errors.phone)}
              margin="normal"
              autoComplete="off"
              InputProps={{
                inputComponent: TextFieldMask.Phone,
              }}
              InputLabelProps={!!formik.values.phone ? { shrink: true } : {}}
            />
            <TextField
              label={t('_common:phone2')}
              name="additionalPhone"
              id="additionalPhone"
              fullWidth
              helperText={formik.touched.additionalPhone && formik.errors.additionalPhone}
              value={formik.values.additionalPhone}
              onChange={formik.handleChange}
              error={formik.touched.additionalPhone && Boolean(formik.errors.additionalPhone)}
              margin="normal"
              autoComplete="off"
              InputProps={{
                inputComponent: TextFieldMask.Phone,
              }}
              InputLabelProps={!!formik.values.additionalPhone ? { shrink: true } : {}}
            />
          </Box>
          <FunctionSelect
            name="function"
            helperText={formik.touched.function && formik.errors.function}
            selected={formik.values.function}
            onChange={(value) => formik.setFieldValue('function', value)}
            error={formik.touched.function && Boolean(formik.errors.function)}
            sx={{
              mb: 2,
            }}
          />
          <TextField
            label={t('_common:email_for_contact')}
            name="email"
            id="email"
            fullWidth
            helperText={formik.touched.email && formik.errors.email}
            value={formik.values.email}
            onChange={formik.handleChange}
            error={formik.touched.email && Boolean(formik.errors.email)}
            autoComplete="off"
            type="email"
            sx={{
              mb: 2,
            }}
          />
          <TextField
            label={t('_common:question_password')}
            name="questionPassword"
            id="questionPassword"
            fullWidth
            helperText={formik.touched.questionPassword && formik.errors.questionPassword}
            value={formik.values.questionPassword}
            onChange={formik.handleChange}
            error={formik.touched.questionPassword && Boolean(formik.errors.questionPassword)}
            autoComplete="off"
          />
          <Box sx={{ display: 'flex', gap: 3 }}>
            <TextField
              label={t('_common:answer_password')}
              name="answerPassword"
              id="answerPassword"
              fullWidth
              helperText={formik.touched.answerPassword && formik.errors.answerPassword}
              value={formik.values.answerPassword}
              onChange={formik.handleChange}
              error={formik.touched.answerPassword && Boolean(formik.errors.answerPassword)}
              autoComplete="off"
              margin="normal"
            />
            <TextField
              label={t('_common:answer_coercion')}
              name="answerCoercion"
              id="answerCoercion"
              fullWidth
              helperText={formik.touched.answerCoercion && formik.errors.answerCoercion}
              value={formik.values.answerCoercion}
              onChange={formik.handleChange}
              error={formik.touched.answerCoercion && Boolean(formik.errors.answerCoercion)}
              autoComplete="off"
              margin="normal"
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <TextField
              label={t('_common:federa_registration')}
              name="taxpayerIdentification"
              id="taxpayerIdentification"
              fullWidth
              helperText={
                formik.touched.taxpayerIdentification && formik.errors.taxpayerIdentification
              }
              value={formik.values.taxpayerIdentification}
              onChange={formik.handleChange}
              error={
                formik.touched.taxpayerIdentification &&
                Boolean(formik.errors.taxpayerIdentification)
              }
              autoComplete="off"
              margin="normal"
              InputProps={{
                inputComponent: TextFieldMask.TaxPayer,
              }}
              InputLabelProps={
                !!formik.values.taxpayerIdentification
                  ? {
                      shrink: true,
                    }
                  : {}
              }
            />
            <TextField
              label={t('_common:national_id')}
              name="identificationNumber"
              id="identificationNumber"
              fullWidth
              helperText={formik.touched.identificationNumber && formik.errors.identificationNumber}
              value={formik.values.identificationNumber}
              onChange={formik.handleChange}
              error={
                formik.touched.identificationNumber && Boolean(formik.errors.identificationNumber)
              }
              autoComplete="off"
              margin="normal"
            />
          </Box>
          <ContactReasonField
            value={formik.values.contactsReason}
            selectItem={async (item, enabled) => {
              formik.setFieldTouched('contactsReason');
              const errors = await formik.setFieldValue(`contactsReason.${item}.enabled`, enabled);
              errors && formik.setErrors(errors);
            }}
            helperText={
              !!formik.touched.contactsReason &&
              (typeof formik.errors.contactsReason === 'string'
                ? formik.errors.contactsReason
                : JSON.stringify(formik.errors.contactsReason))
            }
            error={formik.touched.contactsReason && Boolean(formik.errors.contactsReason)}
          />
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button
            onClick={() => {
              formik.resetForm();
              setOpen(false);
            }}
          >
            {t('_common:cancel')}
          </Button>
          <LoadingButton variant="contained" type="submit" {...{ loading }}>
            {t('_common:save')}
          </LoadingButton>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

export default ContactFormModal;
